import React, { useState, useEffect } from 'react';
import { Card, Typography, List, Switch, Button, Modal, Form, Input, message, Space, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, SettingOutlined } from '@ant-design/icons';
import api from '../../utils/axios';

const { Title, Text } = Typography;

interface Integration {
  id: string;
  name: string;
  description: string;
  status: 'ACTIVE' | 'INACTIVE' | 'ERROR' | 'CONFIGURING';
  type: 'EMAIL' | 'CALENDAR' | 'STORAGE' | 'MASS_EMAIL' | 'PROJECT_MANAGEMENT' | 'CRM' | 'OTHER';
  provider: 'GMAIL' | 'OUTLOOK_365' | 'STANDARD_EMAIL' | 'GOOGLE_CALENDAR' | 'OUTLOOK_CALENDAR' | 'GOOGLE_DRIVE' | 'MICROSOFT_DRIVE' | 'MAILCHIMP' | 'MONDAY_COM' | 'CUSTOM';
  config: Record<string, any>;
  lastSync?: string;
  lastError?: string;
  errorCount?: number;
  createdAt: string;
  updatedAt: string;
}

const Integrations: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);
  const [form] = Form.useForm();

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: Integration[] }>('/integrations');
      if (response.data.success) {
        setIntegrations(response.data.data);
      } else {
        message.error('Failed to fetch integrations');
      }
    } catch (error) {
      console.error('Error fetching integrations:', error);
      message.error('Failed to fetch integrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const handleToggle = async (integration: Integration) => {
    try {
      const newStatus = integration.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await api.put(`/integrations/${integration.id}/status`, { status: newStatus });
      message.success(`Integration ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`);
      fetchIntegrations();
    } catch (error) {
      console.error('Error toggling integration:', error);
      message.error('Failed to update integration status');
    }
  };

  const handleConfigure = (integration: Integration) => {
    setEditingIntegration(integration);
    setIsModalVisible(true);
    form.setFieldsValue(integration.config);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingIntegration) {
        await api.put(`/integrations/${editingIntegration.id}/config`, values);
        message.success('Integration configuration updated successfully');
        setIsModalVisible(false);
        fetchIntegrations();
      }
    } catch (error) {
      console.error('Error saving integration configuration:', error);
      message.error('Failed to save integration configuration');
    }
  };

  const getStatusTag = (status: Integration['status']) => {
    switch (status) {
      case 'ACTIVE':
        return <Tag color="success" icon={<CheckCircleOutlined />}>Active</Tag>;
      case 'INACTIVE':
        return <Tag color="default" icon={<CloseCircleOutlined />}>Inactive</Tag>;
      case 'ERROR':
        return <Tag color="error" icon={<CloseCircleOutlined />}>Error</Tag>;
      case 'CONFIGURING':
        return <Tag color="processing" icon={<SettingOutlined />}>Configuring</Tag>;
      default:
        return null;
    }
  };

  const renderIntegrationConfig = (integration: Integration) => {
    switch (integration.type) {
      case 'EMAIL':
        return (
          <>
            <Form.Item
              name="smtpHost"
              label="SMTP Host"
              rules={[{ required: true, message: 'Please enter SMTP host' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="smtpPort"
              label="SMTP Port"
              rules={[{ required: true, message: 'Please enter SMTP port' }]}
            >
              <Input type="number" />
            </Form.Item>
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true, message: 'Please enter username' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter password' }]}
            >
              <Input.Password />
            </Form.Item>
          </>
        );
      case 'STORAGE':
        return (
          <>
            <Form.Item
              name="accessKey"
              label="Access Key"
              rules={[{ required: true, message: 'Please enter access key' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="secretKey"
              label="Secret Key"
              rules={[{ required: true, message: 'Please enter secret key' }]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="bucket"
              label="Bucket Name"
              rules={[{ required: true, message: 'Please enter bucket name' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="region"
              label="Region"
              rules={[{ required: true, message: 'Please enter region' }]}
            >
              <Input />
            </Form.Item>
          </>
        );
      case 'MASS_EMAIL':
        return (
          <>
            <Form.Item
              name="apiKey"
              label="API Key"
              rules={[{ required: true, message: 'Please enter API key' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="webhookSecret"
              label="Webhook Secret"
              rules={[{ required: true, message: 'Please enter webhook secret' }]}
            >
              <Input.Password />
            </Form.Item>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      <Card>
        <Title level={4}>Integrations</Title>
        <List
          loading={loading}
          dataSource={integrations}
          renderItem={(integration) => (
            <List.Item
              actions={[
                <Button
                  key="configure"
                  icon={<SettingOutlined />}
                  onClick={() => handleConfigure(integration)}
                >
                  Configure
                </Button>,
                <Switch
                  key="status"
                  checked={integration.status === 'ACTIVE'}
                  onChange={() => handleToggle(integration)}
                />,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <span>{integration.name}</span>
                    {getStatusTag(integration.status)}
                  </Space>
                }
                description={integration.description}
              />
              {integration.lastSync && (
                <Text type="secondary">
                  Last synced: {new Date(integration.lastSync).toLocaleString()}
                </Text>
              )}
            </List.Item>
          )}
        />
      </Card>

      <Modal
        title={`Configure ${editingIntegration?.name}`}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          {editingIntegration && renderIntegrationConfig(editingIntegration)}
        </Form>
      </Modal>
    </div>
  );
};

export default Integrations; 